import {
  ComponentFactoryResolver,
  ComponentRef,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewContainerRef,
} from '@angular/core';
import { SearchItem } from '@rivethealth/data/ui-search-item';
import { BehaviorSubject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AnchoredPosition } from '../callout/callout-position';
import { AutoCompleteListComponent } from './autocomplete-list.component';

enum Key {
  ARROW_DOWN = 'ArrowDown',
  ARROW_UP = 'ArrowUp',
  ESCAPE = 'Escape',
  IE_ESCAPE = 'Esc',
  ENTER = 'Enter',
  TAB = 'Tab',
}

const DEBOUNCE_TIME = 100;

@Directive({
  selector: '[rv-autocomplete]',
})
export class AutocompleteDirective implements OnInit {
  constructor(
    private readonly viewRef: ViewContainerRef,
    private readonly cfr: ComponentFactoryResolver,
    private readonly elementRef: ElementRef,
  ) {}
  searchTerm = new BehaviorSubject<string>('');
  listRef: ComponentRef<AutoCompleteListComponent> | null;
  searchTimer?: number;
  targetEl: any;

  ngOnInit() {
    this.searchTerm
      .pipe(debounceTime(DEBOUNCE_TIME), distinctUntilChanged())
      .subscribe(this.doSearch);
  }

  @Input('search') search: (term: string) => Promise<SearchItem[]>;
  @Input('preferredPosition') preferredPosition = AnchoredPosition.BOTTOM_RIGHT;
  @Output('selected') selected = new EventEmitter<SearchItem>();
  @Output('badSelection') badSelection = new EventEmitter<SearchItem>();
  @Output('highlighted') highlighted = new EventEmitter<SearchItem>();

  @HostListener('keyup', ['$event'])
  @HostListener('keydown', ['$event'])
  keyUp($event: any) {
    if (this.listRef) {
      switch ($event.key) {
        case Key.ARROW_UP:
        case Key.ARROW_DOWN:
        case Key.ESCAPE:
        case Key.IE_ESCAPE:
        case Key.ENTER:
        case Key.TAB:
          $event.stopPropagation();
          $event.preventDefault();
          break;
      }
    }
    if ($event.type == 'keydown') {
      return;
    }

    this.targetEl = $event.target;

    switch ($event.key) {
      case Key.ARROW_UP:
      case Key.ARROW_DOWN:
        this.handleArrows($event);
        break;
      case Key.ESCAPE:
      case Key.IE_ESCAPE:
        this.clear();
        break;
      case Key.ENTER:
      case Key.TAB:
        this.handleEnter();
        break;
      default:
        this.searchTerm.next(($event.target && $event.target.value) || '');
        break;
    }
  }

  handleArrows($event: any) {
    if (!this.listRef) return;
    this.listRef.instance.handleArrows($event.key);
  }

  handleEnter() {
    if (
      this.searchTerm.value != '' &&
      (!this.listRef || !this.listRef.instance.handleEnter())
    ) {
      this.badSelection.emit({ text: this.searchTerm.value });
      this.searchTerm.next('');
    }
  }

  @HostListener('blur')
  blur() {
    if (this.searchTerm.value != '' && !this.listRef) {
      this.badSelection.emit({ text: this.searchTerm.value });
      this.searchTerm.next('');
    }
  }

  doSearch = async (searchTerm: string) => {
    if (searchTerm == '') {
      this.clear();
      return;
    }
    if (!this.search) {
      return;
    }
    const results = await this.search(searchTerm);
    if (!results || !results.length) {
      this.clear();
      return;
    }
    results.length = Math.min(results.length, 100);
    this.addList(results);
  };

  clear() {
    if (!this.listRef) {
      return false;
    }
    this.listRef.instance.selected.unsubscribe();
    this.listRef.instance.highlighted.unsubscribe();
    this.listRef.destroy();
    this.listRef = null;
    this.searchTerm.next('');
    return true;
  }

  addList(results: SearchItem[]) {
    if (!this.listRef) {
      const componentFactory = this.cfr.resolveComponentFactory(
        AutoCompleteListComponent,
      );
      this.listRef = this.viewRef.createComponent(componentFactory);
      this.listRef.instance.anchor = this.elementRef.nativeElement;
      this.listRef.instance.preferredPosition = this.preferredPosition;
      this.listRef.instance.width = this.targetEl.offsetWidth + 'px';
      this.listRef.instance.highlighted.subscribe((item: SearchItem) =>
        this.highlighted.emit(item),
      );
      this.listRef.instance.selected.subscribe((item: SearchItem) => {
        this.selected.emit(item);
        this.clear();
      });
      this.listRef.instance.clear.subscribe(() => {
        if (this.searchTerm.value != '') {
          this.badSelection.emit({ text: this.searchTerm.value });
        }
        this.clear();
      });
    }
    this.listRef.instance.list = results;
    this.listRef.instance.render();
  }
}
